import {
  div,
  h1,
  h2,
  fnstate,
  span,
  button,
  table,
  thead,
  tbody,
  tr,
  th,
  td
} from '@srfnstack/fntags'

export const Finance = () => {
  const stats = fnstate({ total_revenue: 0, total_cost: 0, net_profit: 0, loading: true })
  const creditStats = fnstate({ stats: [], recentUsage: [], loading: true })
  const syncing = fnstate(false)

  const fetchStats = async () => {
    stats.assign({ loading: true })
    try {
      const res = await fetch('/api/finance/stats')
      if (res.ok) {
        const data = await res.json()
        stats({ ...data, loading: false })
      }
    } catch (err) {
      console.error('Error fetching stats:', err)
      stats.assign({ loading: false })
    }
  }

  const fetchCreditStats = async () => {
    creditStats.assign({ loading: true })
    try {
      const res = await fetch('/api/finance/credit-stats')
      if (res.ok) {
        const data = await res.json()
        creditStats({ ...data, loading: false })
      }
    } catch (err) {
      console.error('Error fetching credit stats:', err)
      creditStats.assign({ loading: false })
    }
  }

  const syncEtsy = async () => {
    syncing(true)
    try {
      const res = await fetch('/api/finance/sync-etsy', { method: 'POST' })
      if (res.ok) {
        await window.alert('Etsy sync job enqueued!')
      } else {
        await window.alert('Failed to enqueue sync job.')
      }
    } catch (err) {
      console.error('Error syncing Etsy:', err)
    } finally {
      syncing(false)
    }
  }

  fetchStats()

  fetchCreditStats()

  return div({ class: 'p-6 space-y-8' },

    div({ class: 'flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4' },

      h1({ class: 'text-3xl font-bold' }, 'Finance Hub'),

      div({ class: 'flex gap-2 w-full sm:w-auto' },

        button({

          class: 'btn btn-outline btn-sm flex-1 sm:flex-none',

          onclick: () => { fetchStats(); fetchCreditStats() },

          disabled: stats.bindAttr(s => s.loading)

        }, 'Refresh'),

        button({

          class: 'btn btn-primary btn-sm flex-1 sm:flex-none',

          onclick: syncEtsy,

          disabled: syncing.bindAttr()

        }, syncing.bindAs(s => s ? span({ class: 'loading loading-spinner loading-xs' }) : 'Sync Etsy Orders'))

      )

    ),

    // Financial Stats

    stats.bindAs(s => {
      if (s.loading) return div({ class: 'flex justify-center p-12' }, span({ class: 'loading loading-spinner loading-lg' }))

      return div({ class: 'stats stats-vertical lg:stats-horizontal shadow bg-base-200 w-full border border-base-300' },

        div({ class: 'stat' },

          div({ class: 'stat-title' }, 'Total Revenue'),

          div({ class: 'stat-value' }, `${s.total_revenue.toFixed(2)}`),

          div({ class: 'stat-desc' }, 'All-time gross sales')

        ),

        div({ class: 'stat' },

          div({ class: 'stat-title' }, 'Total COGS'),

          div({ class: 'stat-value text-error' }, `${s.total_cost.toFixed(2)}`),

          div({ class: 'stat-desc' }, 'Printify costs + Etsy fees')

        ),

        div({ class: 'stat' },

          div({ class: 'stat-title' }, 'Net Profit'),

          div({ class: 'stat-value ' + (s.net_profit >= 0 ? 'text-success' : 'text-error') }, `${s.net_profit.toFixed(2)}`),

          div({ class: 'stat-desc' }, 'Real-time bottom line')

        )

      )
    }),

    // Credit Analytics

    div({ class: 'space-y-4' },

      h2({ class: 'text-xl font-bold flex items-center gap-2' }, span({ class: 'material-symbols-rounded text-primary' }, 'token'), 'Credit Analytics'),

      div({ class: 'grid grid-cols-1 lg:grid-cols-3 gap-6' },

        // Left: Consumption by Job Type

        div({ class: 'lg:col-span-1' },

          div({ class: 'card bg-base-200 border border-base-300 shadow-sm' },

            div({ class: 'p-4 border-b border-base-300 bg-base-300/30' }, h2({ class: 'font-bold text-xs uppercase tracking-widest opacity-60' }, 'Consumption by Type')),

            div({ class: 'p-0' },

              creditStats.bindAs(cs => {
                if (cs.loading) return div({ class: 'p-8 text-center' }, span({ class: 'loading loading-spinner loading-md' }))

                if (!cs.stats || cs.stats.length === 0) return div({ class: 'p-8 text-center opacity-50 italic text-sm' }, 'No credit usage recorded.')

                return div({ class: 'divide-y divide-base-300' },

                  cs.stats.map(s => div({ class: 'flex justify-between items-center p-4' },

                    div({ class: 'flex flex-col' },

                      span({ class: 'font-bold text-sm' }, s.action_name || s.job_type),

                      span({ class: 'text-[10px] opacity-50' }, `${s.usage_count} jobs total`)

                    ),

                    div({ class: 'flex items-center gap-2' },

                      span({ class: 'text-lg font-black' }, s.total_credits),

                      span({ class: 'text-[10px] opacity-40 uppercase font-bold' }, 'CR')

                    )

                  ))

                )
              })

            )

          )

        ),

        // Right: Recent Detailed Usage

        div({ class: 'lg:col-span-2' },

          div({ class: 'card bg-base-200 border border-base-300 shadow-sm overflow-hidden' },

            div({ class: 'p-4 border-b border-base-300 bg-base-300/30' }, h2({ class: 'font-bold text-xs uppercase tracking-widest opacity-60' }, 'Recent Credit Ledger')),

            div({ class: 'overflow-x-auto' },

              creditStats.bindAs(cs => {
                if (cs.loading) return div({ class: 'p-12 text-center' }, span({ class: 'loading loading-spinner loading-lg text-primary' }))

                if (!cs.recentUsage || cs.recentUsage.length === 0) return div({ class: 'p-12 text-center opacity-50' }, 'No usage history found.')

                return table({ class: 'table w-full' },

                  thead(tr(th('Date'), th('Action'), th('Credits'))),

                  tbody(

                    cs.recentUsage.map(u => tr(

                      td({ class: 'text-[10px] font-mono' }, new Date(u.created_at).toLocaleString()),

                      td(div({ class: 'flex flex-col' },

                        span({ class: 'font-bold text-sm' }, u.action_name || u.job_type),

                        u.payload ? span({ class: 'text-[9px] opacity-40 truncate max-w-[200px]' }, JSON.stringify(u.payload)) : ''

                      )),

                      td(span({ class: 'badge badge-ghost font-bold' }, `-${u.credits}`))

                    ))

                  )

                )
              })

            )

          )

        )

      )

    )

  )
}
